import React, { useState, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import IconButton from '../ui/IconButton';
import { Plus } from 'lucide-react';
import OfferingCard from './OfferingCard';
import EditOfferingModal from './EditOfferingModal';
import useMediaQuery from '../../hooks/useMediaQuery';
import { Text } from '../ui';

const OfferingsForm = ({ initialData, onSave, isLoading, onDelete, onAdd, isOnboarding }) => {
  const { isMobile, isSmallMobile } = useMediaQuery();
  const isMobileView = isMobile || isSmallMobile;

  // Handle both formats: direct array or object with offerings property
  const getOfferingsArray = (data) => {
    if (Array.isArray(data)) {
      return data.length > 0 ? data : [{ id: '', name: '', description: '', page_url: '' }];
    }
    if (data?.offerings && Array.isArray(data.offerings)) {
      return data.offerings.length > 0
        ? data.offerings
        : [{ id: '', name: '', description: '', page_url: '' }];
    }
    return [{ id: '', name: '', description: '', page_url: '' }];
  };

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { isDirty },
  } = useForm({
    defaultValues: {
      offerings: getOfferingsArray(initialData),
    },
  });

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'offerings',
  });

  const onSubmit = (index, data) => {
    onSave(index, data);
  };

  // const [draggedIndex, setDraggedIndex] = useState(null);
  // const [draggedOverIndex, setDraggedOverIndex] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [offeringToDelete, setOfferingToDelete] = useState(null);

  // Watch all form values to detect changes
  const offerings = watch('offerings');

  // Update form when initialData changes
  useEffect(() => {
    if (initialData) {
      reset({ offerings: getOfferingsArray(initialData) });
    }
  }, [initialData, reset]);

  const handleSaveOffering = (index, updatedOffering) => {
    // save the single offering change
    handleSubmit((data) => onSubmit(index, updatedOffering))();
  };

  const handleDeleteOffering = (id) => {
    if (fields.length > 1) {
      const offering = offerings.find((off) => off.id === id);
      setOfferingToDelete(offering);
      setIsDeleteModalOpen(true);
    }
  };

  const handleConfirmDelete = (id) => {
    const index = offerings.findIndex((offering) => offering.id === id);
    remove(index);
    // Auto-save after deletion
    handleSubmit(() => onDelete(id))();
    setIsDeleteModalOpen(false);
    setOfferingToDelete(null);
  };

  const handleAddOffering = () => {
    setIsAddModalOpen(true);
  };

  const handleAddSubmit = (data) => {
    // Add the new offering
    const newIndex = offerings.length;
    append(data);
    // Save it
    handleSubmit(() => onAdd(data))();
  };

  // const handleDragStart = (e, index) => {
  //   setDraggedIndex(index);
  //   e.dataTransfer.effectAllowed = 'move';
  // };

  // const handleDragOver = (e, index) => {
  //   e.preventDefault();
  //   e.dataTransfer.dropEffect = 'move';
  //   if (draggedIndex !== index) {
  //     setDraggedOverIndex(index);
  //   }
  // };

  // const handleDragEnter = (e, index) => {
  //   e.preventDefault();
  //   if (draggedIndex !== index) {
  //     setDraggedOverIndex(index);
  //   }
  // };

  // const handleDragLeave = (e, index) => {
  //   e.preventDefault();
  //   setDraggedOverIndex(null);
  // };

  // const handleDrop = (e, dropIndex) => {
  //   e.preventDefault();

  //   if (draggedIndex === null || draggedIndex === dropIndex) {
  //     setDraggedIndex(null);
  //     setDraggedOverIndex(null);
  //     return;
  //   }

  //   // Use react-hook-form's move function
  //   move(draggedIndex, dropIndex);

  //   // Save the reordered offerings
  //   const newOfferings = [...offerings];
  //   const draggedItem = newOfferings[draggedIndex];
  //   newOfferings.splice(draggedIndex, 1);
  //   newOfferings.splice(dropIndex, 0, draggedItem);

  //   onSave({ offerings: newOfferings });

  //   setDraggedIndex(null);
  //   setDraggedOverIndex(null);
  // };

  return (
    <div className='tour-offerings-section rounded-lg shadow-sm p-2 md:p-6 h-full flex flex-col min-w-0' style={{ backgroundColor: 'var(--bg-card)', border: '1px solid var(--border)' }}>
      <div className='flex justify-between items-center mb-6 shrink-0'>
        <div>
          <Text variant='title' className={`${isMobileView ? 'text-base' : ''}`} style={{ color: 'var(--text-primary)' }}>
            Products & Services
          </Text>
          <Text
            variant='text-secondary'
            className={`mt-1 ${isMobileView ? 'text-xs' : ''}`}
            style={{ color: 'var(--text-muted)' }}
          >
            Manage your offerings and pricing
          </Text>
        </div>
        <div className='flex items-center gap-2 pr-2'>
          <IconButton
            icon={() => <Plus />}
            tooltip='Add offering'
            onClick={handleAddOffering}
            variant='ghostPrimary'
            className='tour-add-offering-button'
          />
        </div>
      </div>

      <div
        className='space-y-4 overflow-y-auto pr-2 flex-1 min-w-0 scrollbar-thin scrollbar-thumb-slate-200 scrollbar-track-transparent hover:scrollbar-thumb-slate-300'
        style={{ WebkitOverflowScrolling: 'touch' }}
      >
        {fields.map((field, index) => (
          <OfferingCard
            key={field.id}
            offering={offerings[index]}
            index={index}
            isMobile={isMobileView}
            onSave={handleSaveOffering}
            onDelete={fields.length > 1 ? handleDeleteOffering : null}
            // onDragStart={handleDragStart}
            // onDragOver={handleDragOver}
            // onDragEnter={handleDragEnter}
            // onDragLeave={handleDragLeave}
            // onDrop={handleDrop}
            // isDragging={draggedIndex === index}
            // isDraggedOver={draggedOverIndex === index}
          />
        ))}
      </div>

      <EditOfferingModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        offering={null}
        onSave={handleAddSubmit}
        mode='add'
      />

      <EditOfferingModal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setOfferingToDelete(null);
        }}
        offering={offeringToDelete}
        onSave={handleConfirmDelete}
        mode='delete'
      />
    </div>
  );
};

export default OfferingsForm;
